/**
 * Menu Form Page - Add/Edit Menu Items
 * 
 * Form for creating and editing menu items.
 * Saves to /api/menu endpoint.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiArrowLeft, HiCheck } from 'react-icons/hi';
import { InfoTooltip } from '../../components/Tooltip';
import ImageSelector from '../components/ImageSelector';
import { adminFetch, adminHeaders } from '../lib/adminApi';

/** Category from API – admin sees all (active + inactive) for dropdown */
interface MenuCategoryOption {
  id: string;
  name: string;
  display_order: number;
  is_active: boolean;
  icon: string | null;
}

interface FormData {
  name: string;
  description: string;
  price: string;
  category: string;
  image_url: string;
  is_available: boolean;
  is_featured: boolean;
  is_orderable_online: boolean;
  is_popular: boolean;
  is_gluten_free:      boolean;
  /** When true, shows an amber "Limited" chip on homepage product cards */
  is_limited_edition:  boolean;
  /** 'auto' = use category rules; 'true' = always apply HST; 'false' = never apply HST */
  tax_applies: 'auto' | 'true' | 'false';
  /** Last day customers may order (YYYY-MM-DD). Empty = no seasonal cutoff. */
  order_by: string;
  /** Last day customers may pick up (YYYY-MM-DD). Empty = no seasonal cutoff. */
  pickup_through: string;
}

export default function MenuForm() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(itemId);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    price: '',
    category: '',
    image_url: '',
    is_available: true,
    is_featured: false,
    is_orderable_online: false,
    is_popular:         false,
    is_gluten_free:     false,
    is_limited_edition: false,
    tax_applies: 'auto',
    order_by: '',
    pickup_through: '',
  });

  const [loading, setLoading] = useState(false);
  const [fetchingItem, setFetchingItem] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [categories, setCategories] = useState<MenuCategoryOption[]>([]);

  // Fetch categories for dropdown (admin: all, active first by display_order)
  useEffect(() => {
    adminFetch('/api/menu-categories')
      .then(res => res.ok ? res.json() : { categories: [] })
      .then(data => setCategories(data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  // Fetch existing item when editing
  useEffect(() => {
    if (isEditing && itemId) {
      setFetchingItem(true);
      adminFetch(`/api/menu/${itemId}`)
        .then(res => {
          if (!res.ok) throw new Error('Item not found');
          return res.json();
        })
        .then(data => {
          setFormData({
            name: data.name || '',
            description: data.description || '',
            price: data.price?.toString() || '',
            category: data.category || '',
            image_url: data.image_url || '',
            is_available: data.is_available ?? true,
            is_featured: data.is_featured ?? false,
            is_orderable_online: data.is_orderable_online ?? false,
            is_popular:         data.is_popular          ?? false,
            is_gluten_free:     data.is_gluten_free      ?? false,
            is_limited_edition: data.is_limited_edition  ?? false,
            tax_applies: data.tax_applies === true ? 'true' : data.tax_applies === false ? 'false' : 'auto',
            order_by: data.order_by ? String(data.order_by).split('T')[0] : '',
            pickup_through: data.pickup_through ? String(data.pickup_through).split('T')[0] : '',
          });
        })
        .catch(err => {
          setError(err.message);
        })
        .finally(() => {
          setFetchingItem(false);
        });
    }
  }, [isEditing, itemId]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const url = isEditing ? `/api/menu/${itemId}` : '/api/menu';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          price: parseFloat(formData.price),
          category: formData.category,
          image_url: formData.image_url.trim() || null,
          is_available: formData.is_available,
          is_featured: formData.is_featured,
          is_orderable_online: formData.is_orderable_online,
          is_popular:         formData.is_popular,
          is_gluten_free:     formData.is_gluten_free,
          is_limited_edition: formData.is_limited_edition,
          tax_applies: formData.tax_applies === 'true' ? true : formData.tax_applies === 'false' ? false : null,
          order_by: formData.order_by.trim() || null,
          pickup_through: formData.pickup_through.trim() || null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save');
      }

      setSuccess(true);
      
      // Navigate back after short delay to show success
      setTimeout(() => {
        navigate('/admin/menu');
      }, 1000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  // Loading state while fetching existing item
  if (fetchingItem) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading item...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/menu')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Back to Menu"
          aria-label="Back to Menu"
        >
          <HiArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Menu Item' : 'Add Menu Item'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditing ? 'Update item details' : 'Add a new item to your menu'}
          </p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <HiCheck className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="font-medium text-green-800">
              {isEditing ? 'Item updated!' : 'Item created!'}
            </p>
            <p className="text-sm text-green-600">Redirecting to menu list...</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700 font-medium">❌ {error}</p>
          <p className="text-red-600 text-sm mt-1">
            Tip: Try refreshing the page and try again. If this keeps happening, contact support.
          </p>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Item Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="e.g., Chocolate Fudge Cake"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg
                       focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            placeholder="Describe this delicious item..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg
                       focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>

        {/* Price and Category row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Price */}
          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
              Price (CAD) *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg
                           focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              Category *
              <InfoTooltip content="Choose which category this product belongs to. This helps customers find items and organizes your menu." />
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
                         focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">Select category</option>
              {[...categories]
                .sort((a, b) => {
                  if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
                  return a.display_order - b.display_order;
                })
                .map(cat => (
                  <option key={cat.id} value={cat.name}>
                    {cat.name}{!cat.is_active ? ' (hidden)' : ''}
                  </option>
                ))}
              {formData.category && !categories.some(c => c.name === formData.category) && (
                <option value={formData.category}>{formData.category} (legacy)</option>
              )}
            </select>
          </div>

          {/* Tax override - for items where category rules are unsure */}
          <div>
            <label htmlFor="tax_applies" className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
              HST (tax) for this item
              <InfoTooltip content="Default: use category rules (6+ baked items exempt, cakes exempt, sandwiches always taxable). Override only when you need to confirm: Always taxable = HST always applies. Never taxable = HST never applies." />
            </label>
            <select
              id="tax_applies"
              name="tax_applies"
              value={formData.tax_applies}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
                         focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="auto">Auto (use category rules)</option>
              <option value="true">Always taxable</option>
              <option value="false">Never taxable</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Leave on Auto unless you need to override for this product.</p>
          </div>
        </div>

        {/* Image Upload - Using ImageSelector component */}
        <ImageSelector
          value={formData.image_url}
          onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
          label="Product Image"
          helpText="Browse, upload (optional crop), or paste URL. Menu cards stay square. On the product page the full photo shows unless you pick Fill square. Click preview to set focal point. Rotate 90° here or via All Images."
          previewHeight="h-32"
          previewAspect="square"
        />

        {/* Toggles - Visibility & Display Options */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 border-b pb-2">Display Options</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Available on Menu */}
            <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
              <input
                type="checkbox"
                name="is_available"
                checked={formData.is_available}
                onChange={handleChange}
                className="w-5 h-5 mt-0.5 text-purple-600 border-gray-300 rounded
                           focus:ring-purple-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Show on Menu</span>
                <p className="text-xs text-gray-500">Item appears on the Menu page</p>
              </div>
            </label>

            {/* Featured in Gallery */}
            <label className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors">
              <input
                type="checkbox"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
                className="w-5 h-5 mt-0.5 text-amber-600 border-gray-300 rounded
                           focus:ring-amber-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  ⭐ Featured Gallery
                  <InfoTooltip content="When checked, this product appears in the featured gallery on your homepage. Great for showcasing your best items!" />
                </span>
                <p className="text-xs text-gray-500">Shows in homepage featured gallery</p>
              </div>
            </label>

            {/* Available for Online Ordering */}
            <label className="flex items-start gap-3 p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
              <input
                type="checkbox"
                name="is_orderable_online"
                checked={formData.is_orderable_online}
                onChange={handleChange}
                className="w-5 h-5 mt-0.5 text-green-600 border-gray-300 rounded
                           focus:ring-green-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  🛒 Online Ordering
                  <InfoTooltip content="When checked, customers can add this item to their cart and order it online. Uncheck for items that are only available in-store or require special approval." />
                </span>
                <p className="text-xs text-gray-500">Customers can order this online</p>
              </div>
            </label>
          </div>

          {/* Seasonal preorder window — used for holiday pies, trays, etc. */}
          <div className="mt-2 p-4 bg-orange-50 rounded-lg border border-orange-100">
            <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
              Seasonal order window
              <InfoTooltip content="For holiday items (Thanksgiving pies, Christmas trays, etc.). After Order by, the item hides from the public menu and cannot be ordered. Leave blank for year-round items." />
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Optional. Set both dates when this item is only offered for a holiday window.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Order by (last day)</label>
                <input
                  type="date"
                  name="order_by"
                  value={formData.order_by}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Pickup through (last day)</label>
                <input
                  type="date"
                  name="pickup_through"
                  value={formData.pickup_through}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* Popular Treats */}
            <label className="flex items-start gap-3 p-3 bg-pink-50 rounded-lg cursor-pointer hover:bg-pink-100 transition-colors">
              <input
                type="checkbox"
                name="is_popular"
                checked={formData.is_popular}
                onChange={handleChange}
                className="w-5 h-5 mt-0.5 text-pink-600 border-gray-300 rounded
                           focus:ring-pink-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  🔥 Popular Treat
                  <InfoTooltip content="When checked, this product appears in the 'Popular Treats' section on your homepage. Use this for your best-selling items!" />
                </span>
                <p className="text-xs text-gray-500">Shows in "Popular Treats" on homepage</p>
              </div>
            </label>

            {/* Gluten free */}
            <label className="flex items-start gap-3 p-3 bg-amber-50/80 rounded-lg cursor-pointer hover:bg-amber-100/80 transition-colors">
              <input
                type="checkbox"
                name="is_gluten_free"
                checked={formData.is_gluten_free}
                onChange={handleChange}
                className="w-5 h-5 mt-0.5 text-amber-600 border-gray-300 rounded
                           focus:ring-amber-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  🌾 Gluten free
                  <InfoTooltip content="When checked, this item shows a 'Gluten free' badge on the menu and appears when customers use the 'Gluten free only' filter. Use for cookies, bread, bars, etc. that are gluten free." />
                </span>
                <p className="text-xs text-gray-500">Badge + filter on public menu</p>
              </div>
            </label>

            {/* Limited edition */}
            <label className="flex items-start gap-3 p-3 bg-orange-50/80 rounded-lg cursor-pointer hover:bg-orange-100/80 transition-colors">
              <input
                type="checkbox"
                name="is_limited_edition"
                checked={formData.is_limited_edition}
                onChange={handleChange}
                className="w-5 h-5 mt-0.5 text-orange-600 border-gray-300 rounded
                           focus:ring-orange-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  ⏳ Limited edition
                  <InfoTooltip content="When checked, homepage product cards show an amber 'Limited' badge. Use for seasonal specials, small-batch items, or anything in short supply to drive urgency." />
                </span>
                <p className="text-xs text-gray-500">Shows "Limited" urgency chip on homepage cards</p>
              </div>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/admin/menu')}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg
                       hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || success}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg
                       hover:bg-purple-700 transition-colors font-medium
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : success ? 'Saved!' : isEditing ? 'Update Item' : 'Add Item'}
          </button>
        </div>
      </form>
    </div>
  );
}
